import React, { useState } from 'react'
import { Control, Controller } from 'react-hook-form'

// Tooltip from MUI, NOT @repo/ui — that one is a 0-byte stub that resolves in
// tsc and crashes at runtime.
import { alpha, Box, Checkbox, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { ArrowUpRightIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react'

import { Alert, Button, Card, Input, TOOLBAR_CONTROL_HEIGHT } from '@repo/ui'
// The deep path, not the `components` barrel: this file now lives INSIDE that
// barrel, so importing through it would be circular.
import { Slideshow } from 'components/display/Slideshow'

import { IMPORT_FIELD_LABELS } from '@repo/types'

import { openFieldGaps, ProductFormRow, ProductPickerFormValues } from './types'

/**
 * Big enough to recognise a handbag from, small enough that five rows still fit
 * a 720px modal without becoming a scroll marathon. The row's height is fixed by
 * this square, so a product with no images and one with nine occupy identically —
 * the Slideshow keeps a 1:1 box either way and its thumbnail strip is off.
 */
const PREVIEW_SIZE = 132

interface ProductPickerProps {
  control: Control<ProductPickerFormValues>
  /**
   * Stable RHF field identities. Values that change come from `rows`.
   *
   * Typed as bare `{id}` rather than `FieldArrayWithId<…>`: the id is the only
   * thing read here (React key + the expanded-overrides map), so the exact shape
   * is what the component uses and a caller with a wider form needs no cast for
   * this prop.
   */
  fields: Array<{ id: string }>
  /** The watched values — `fields` is a snapshot and goes stale on edit. */
  rows: ProductFormRow[]
  onToggle: (index: number) => void
  onRemove: (index: number) => void
  onSetAllSelected: (selected: boolean) => void
  onAddManual: () => void
  importUrl: string
  onImportUrlChange: (value: string) => void
  onImport: () => void
  isImporting: boolean
  importError: string | null
  /**
   * VRG-716 — a non-error note under the field, e.g. "Already in the list: X". Pasting a product
   * that is already listed adds nothing; this says why nothing appeared.
   */
  importNotice?: string | null
  /** Shown in place of the list when it is empty. Defaults to the create-demo copy. */
  emptyText?: string
}

/**
 * VRG-311 — the AE's product review, rebuilt from a repeating form into a
 * pick-list over what the seeder found.
 *
 * The form it replaced could only express "these are the products", so removing
 * a bad scrape destroyed it and there was no way back short of re-proposing the
 * whole site. Reviewing a catalogue is a selection problem, not a data-entry one:
 * the AE's actual job here is to look at five things and say which two are wrong.
 * So every candidate stays listed and the checkbox is the whole interaction, with
 * editing demoted behind a control for the rarer case where a scraped title needs
 * fixing.
 *
 * VRG-369 — lifted out of `CreateDemoModal` when a second consumer appeared: the
 * Demos console's Add-products modal, which does the same job against a demo that
 * already exists. Its contract narrowed to `ProductPickerFormValues` in the move
 * (see `types.ts`); nothing else about it changed.
 */
export const ProductPicker = ({
  control,
  fields,
  rows,
  onToggle,
  onRemove,
  onSetAllSelected,
  onAddManual,
  importUrl,
  onImportUrlChange,
  onImport,
  isImporting,
  importError,
  importNotice,
  emptyText = 'No products yet. Paste a listing URL above, or add one by hand. At least one is needed before you can issue.',
}: ProductPickerProps) => {
  // Which rows have their fields open. Keyed by RHF field id and holding only
  // EXPLICIT choices, so the default can stay derived: a hand-added row opens
  // (there is nothing to review, only to type), a scraped one starts closed.
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({})

  const selectedCount = rows.filter((row) => row?.selected).length
  const allSelected = fields.length > 0 && selectedCount === fields.length

  return (
    <Card
      title="Products"
      action={
        <Button
          variant="secondary"
          startIcon={<PlusIcon strokeWidth={1} size={16} />}
          onClick={onAddManual}
        >
          Add product
        </Button>
      }
    >
      <Stack spacing={4}>
        {/* VRG-308 AC3. Sits ABOVE the list because it is the faster path: an AE
            with a product page in hand should not have to scroll past the
            candidates to find it. What it imports lands at the top of the list,
            ticked. */}
        <Stack spacing={2}>
          <Stack direction="row" spacing={3} alignItems="flex-end">
            <Box sx={{ flex: 1 }}>
              <Input
                label="Add a product by pasting its listing URL"
                placeholder="https://brand.com/products/the-bag"
                value={importUrl}
                // @repo/ui's Input hands onChange the VALUE, not the event, and
                // exposes no onKeyDown — so submit-on-Enter isn't available and
                // the Import button is the affordance.
                onChange={onImportUrlChange}
              />
            </Box>
            <Button
              variant="secondary"
              onClick={onImport}
              loading={isImporting}
              disabled={!importUrl.trim()}
              // Match the field beside it. §29's band rule: a control sharing a
              // row with a field matches the FIELD, not the default button
              // height. Measured — @repo/ui's Input renders at 49, which is
              // TOOLBAR_CONTROL_HEIGHT, NOT the 40 of CONTROL_HEIGHT. The two
              // tokens are 9px apart and the name does not tell you which a
              // given field uses, so measure rather than infer.
              sx={{ height: TOOLBAR_CONTROL_HEIGHT, flexShrink: 0 }}
            >
              Import
            </Button>
          </Stack>
          {importError && (
            <Alert severity="warning" sx={{ lineHeight: 1.5 }}>
              {importError}
            </Alert>
          )}
          {!importError && importNotice && (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }} role="status">
              {importNotice}
            </Typography>
          )}
        </Stack>

        {fields.length === 0 ? (
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {emptyText}
          </Typography>
        ) : (
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body3" color="text.secondary">
                {selectedCount} of {fields.length} selected
              </Typography>
              {/* VRG-716 — a text button: a list-level toggle, not an action of the card. Same
                  treatment as the delete-certificate modal's Select all / Deselect all. */}
              <Button variant="text" onClick={() => onSetAllSelected(!allSelected)}>
                {allSelected ? 'Clear all' : 'Select all'}
              </Button>
            </Stack>

            <Stack spacing={3}>
              {fields.map((field, index) => {
                const row = rows[index]
                if (!row) return null
                // A selected row with no title blocks the issue, so its fields
                // are forced open and cannot be collapsed away — otherwise the
                // validation error lives inside a closed disclosure and the
                // Issue button just refuses with nothing on screen to explain it.
                const blocksIssue = row.selected && !row.title?.trim()
                const isExpanded = blocksIssue || (expandedOverrides[field.id] ?? row.manual)
                const setExpanded = (value: boolean) =>
                  setExpandedOverrides((prev) => ({ ...prev, [field.id]: value }))

                return (
                  <Box
                    key={field.id}
                    sx={(theme) => ({
                      borderRadius: 2,
                      // §34, selectable card: the tint means "considering", the
                      // brand BORDER means "chosen". Hover must not touch the
                      // border or it becomes indistinguishable from selected,
                      // which is the whole state this row exists to convey.
                      border: `1px solid ${
                        row.selected ? theme.palette.brand[500] : theme.palette.primary[100]
                      }`,
                      backgroundColor: row.selected
                        ? alpha(theme.palette.brand[500], 0.1)
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: row.selected
                          ? alpha(theme.palette.brand[500], 0.1)
                          : alpha(theme.palette.brand[500], 0.06),
                      },
                    })}
                  >
                    <Stack
                      direction="row"
                      spacing={3}
                      alignItems="flex-start"
                      onClick={() => onToggle(index)}
                      sx={{ p: 3, cursor: 'pointer' }}
                    >
                      <Checkbox
                        checked={row.selected}
                        onChange={() => onToggle(index)}
                        onClick={(event) => event.stopPropagation()}
                        size="small"
                        inputProps={{
                          'aria-label': row.title
                            ? `Include ${row.title} in this demo`
                            : 'Include this product in this demo',
                        }}
                        sx={{ mt: '-2px' }}
                      />

                      <Box
                        sx={{ width: PREVIEW_SIZE, flexShrink: 0 }}
                        // The preview is interactive — arrows to page through the
                        // images, and the slide itself is draggable. Without this
                        // every one of those clicks bubbles to the row and toggles
                        // the checkbox, so looking at a product's second photo
                        // silently drops it from the demo. Caught in the browser;
                        // the arrows are the one control here you use REPEATEDLY,
                        // which is what made it bad rather than merely wrong.
                        onClick={(event) => event.stopPropagation()}
                      >
                        {/* Fixed 1:1 box with the thumbnail strip suppressed, so
                            image count cannot change the row's height. Slideshow
                            already renders a neutral placeholder for an empty
                            set, so a product with no images needs no branch. */}
                        <Slideshow images={row.images ?? []} showThumbs={false} />
                      </Box>

                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.title?.trim() || 'Untitled product'}
                        </Typography>
                        {row.sku?.trim() && (
                          <Typography variant="body3" color="text.secondary">
                            {row.sku}
                          </Typography>
                        )}
                        {row.description?.trim() && (
                          <Typography
                            variant="body3"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {row.description}
                          </Typography>
                        )}
                        {/* VRG-723 — the import field report: what could not be pulled in for
                            THIS product, and why, so the AE fills it before issuing. */}
                        {openFieldGaps(row).length > 0 && (
                          <Stack spacing={0.5} sx={{ pt: 1 }} data-testid="product-field-report">
                            <Typography variant="body3" sx={{ fontWeight: 500 }}>
                              Not pulled in
                            </Typography>
                            {openFieldGaps(row).map((gap) => (
                              <Typography key={gap.field} variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                <Box component="span" sx={{ color: 'text.primary' }}>
                                  {IMPORT_FIELD_LABELS[gap.field]}:
                                </Box>{' '}
                                {gap.reason}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexShrink: 0 }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Tooltip title={isExpanded ? 'Hide details' : 'Edit details'}>
                          <IconButton
                            aria-label={isExpanded ? 'Hide details' : 'Edit details'}
                            size="small"
                            onClick={() => setExpanded(!isExpanded)}
                          >
                            <PencilIcon strokeWidth={1} size={16} />
                          </IconButton>
                        </Tooltip>

                        {row.productUrlKind !== 'none' && (
                          <Tooltip
                            title={
                              row.productUrlKind === 'page'
                                ? 'Open the listing in a new tab'
                                : 'Find this product on the store in a new tab'
                            }
                          >
                            <IconButton
                              aria-label="Open the product page in a new tab"
                              size="small"
                              component="a"
                              href={row.productUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ArrowUpRightIcon strokeWidth={1} size={16} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* On EVERY row, not just hand-added ones.
                            The checkbox and the X are different intents and both
                            are wanted: untick = "not this one, but keep it where
                            I can change my mind", X = "take it off the list".
                            Restricting X to manual rows meant a scraped catalogue
                            of five could only ever be narrowed by leaving four
                            greyed-out rows in the way.
                            Both are set-removal, so both are an X and neither
                            confirms (§38) — re-proposing restores a scraped row. */}
                        <Tooltip title="Remove from the list">
                          <IconButton
                            aria-label="Remove this product from the list"
                            size="small"
                            onClick={() => onRemove(index)}
                          >
                            <XIcon strokeWidth={1} size={16} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    {isExpanded && (
                      <Stack
                        spacing={3}
                        sx={(theme) => ({
                          px: 3,
                          pb: 3,
                          pt: 3,
                          borderTop: `1px solid ${theme.palette.primary[100]}`,
                        })}
                      >
                        <Controller
                          name={`products.${index}.title`}
                          control={control}
                          rules={{ validate: (value) => !row.selected || !!value?.trim() }}
                          render={({ field: titleField, fieldState }) => (
                            <Input
                              label="Title"
                              placeholder="Product title"
                              value={titleField.value}
                              onValueChange={titleField.onChange}
                              error={!!fieldState.error}
                              helperText={
                                fieldState.error ? 'A selected product needs a title' : undefined
                              }
                            />
                          )}
                        />
                        <Stack direction="row" spacing={3}>
                          <Box sx={{ flex: 1 }}>
                            <Controller
                              name={`products.${index}.sku`}
                              control={control}
                              render={({ field: skuField }) => (
                                <Input
                                  label="SKU"
                                  placeholder="Optional"
                                  value={skuField.value}
                                  onValueChange={skuField.onChange}
                                />
                              )}
                            />
                          </Box>
                          <Box sx={{ flex: 2 }}>
                            <Controller
                              name={`products.${index}.description`}
                              control={control}
                              render={({ field: descriptionField }) => (
                                <Input
                                  label="Description"
                                  placeholder="Optional"
                                  value={descriptionField.value}
                                  onValueChange={descriptionField.onChange}
                                />
                              )}
                            />
                          </Box>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                )
              })}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
